import React, { useState, forwardRef, useEffect, useCallback, useRef } from "react";
import { useMaskito } from "@maskito/react";
import { maskitoDateOptionsGenerator } from "@maskito/kit";
import { DateTime } from "luxon";
import { getDateTime } from "../../utils/data";
import { Capacitor } from "@capacitor/core";
import { DatePicker } from "@capacitor-community/date-picker";
import { ImageIcon } from "../ImageIcon";

interface CustomDatePickerProps {
  selectedDate: Date | null;
  setSelectedDate: (date: Date | null) => void;
  id?: string;
}

const INPUT_FORMAT = "MM/dd/yyyy";

const CustomDatePicker: React.FC<CustomDatePickerProps> = ({
  selectedDate,
  setSelectedDate,
  id,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [date, setDate] = useState("");
  const [showCalendar, setShowCalendar] = useState(false);
  const datePickerRef = useRef<HTMLDivElement>(null);

  const maskOptions = maskitoDateOptionsGenerator({
    mode: "mm/dd/yyyy",
    separator: "/",
  });

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target as Node)) {
        setShowCalendar(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const maskRef = useMaskito({ options: maskOptions });

  useEffect(() => {
    if (selectedDate) {
      const formattedDate = DateTime.fromJSDate(selectedDate).toFormat(INPUT_FORMAT);
      if (formattedDate !== inputValue) {
        setInputValue(formattedDate);
      }
    } else if (inputValue !== "") {
      setInputValue("");
    }
  }, [selectedDate]);

  const handleShowDatePicker = async () => {
    if (Capacitor.getPlatform() === "web") {
      setShowCalendar(!showCalendar);
    } else {
      // Despite what the type definition says, empty strings crash the date picker.
      // The joys of Open Source Plugins.
      let formattedValue = inputValue === "" ? undefined : inputValue;
      if (formattedValue) {
        // I have yet to figure out why, but it seems like the DatePicker plugin on Android
        // does NOT return the date value in the format specified in the config and will always
        // return an ISO string. So, the date picker will only receive and return ISO strings for
        // both mobile OS platforms to avoid this problem, else the app will crash.
        formattedValue = DateTime.fromFormat(formattedValue, INPUT_FORMAT).toISO() ?? undefined;
      }

      const { value } = await DatePicker.present({
        mode: "date",
        locale: "en-US",
        date: formattedValue,
        ios: {
          style: "wheels",
        },
        android: {
          mode: "date",
        },
      });

      if (value) {
        setSelectedDate(DateTime.fromISO(value).toJSDate());
      }
    }
  };

  const handleInputChange = (e: React.FormEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value;
    setInputValue(value);

    if (value.length === 10) {
      const date = getDateTime(value);
      if (date.isValid) {
        setSelectedDate(date.toJSDate());
      }
    } else if (value === "") {
      setSelectedDate(null);
    }
  };

  const handleDateSelect = (selectedDate: Date) => {
    setDate(
      selectedDate.toLocaleDateString("en-US", {
        month: "2-digit",
        day: "2-digit",
        year: "numeric",
      })
    );
    setShowCalendar(false);
  };

  const SimpleCalendar = () => {
    const [currentDate, setCurrentDate] = useState(
      selectedDate ? new Date(selectedDate) : new Date()
    );

    const daysInMonth = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth() + 1,
      0
    ).getDate();
    const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1).getDay();

    const handlePrevMonth = () => {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
    };

    const handleNextMonth = () => {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
    };

    const handleDateClick = (clickedDate: Date) => {
      setSelectedDate(clickedDate);
      setInputValue(DateTime.fromJSDate(clickedDate).toFormat(INPUT_FORMAT));
      handleDateSelect(clickedDate);
    };

    const isDateSelected = (day: number) => {
      return (
        selectedDate &&
        day === selectedDate.getDate() &&
        currentDate.getMonth() === selectedDate.getMonth() &&
        currentDate.getFullYear() === selectedDate.getFullYear()
      );
    };

    return (
      <div className="bg-base-100 p-2 border-2 border-info rounded-[var(--radius-box)]">
        <div className="flex justify-between items-center">
          <button
            className="btn btn-square"
            onClick={(e) => {
              e.preventDefault();
              handlePrevMonth();
            }}
          >
            <span className="fa-solid fa-caret-left"></span>
          </button>
          <span>{currentDate.toLocaleDateString("en-US", { month: "long", year: "numeric" })}</span>
          <button
            className="btn btn-square"
            onClick={(e) => {
              e.preventDefault();
              handleNextMonth();
            }}
          >
            <span className="fa-solid fa-caret-right"></span>
          </button>
        </div>

        <div className="grid grid-cols-7 gap-1">
          {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((day) => (
            <div key={day} style={{ textAlign: "center", fontWeight: "bold" }}>
              {day}
            </div>
          ))}
          {Array.from({ length: firstDayOfMonth }).map((_, index) => (
            <div key={`empty-${index}`} />
          ))}
          {Array.from({ length: daysInMonth }).map((_, index) => {
            const day = index + 1;
            const isSelected = isDateSelected(day);
            return (
              <button
                key={day}
                onClick={() =>
                  handleDateClick(new Date(currentDate.getFullYear(), currentDate.getMonth(), day))
                }
                data-selected={isSelected ? "true" : undefined}
                className="btn btn-square [&:not([data-selected])]:!bg-base-100 [&:not([data-selected])]:text-base-content data-selected:!bg-primary data-selected:text-primary-content"
              >
                {day}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div style={{ position: "relative" }} ref={datePickerRef}>
      <div className="flex gap-4">
        <input
          id={id}
          type="text"
          value={inputValue}
          onInput={handleInputChange}
          ref={maskRef}
          placeholder={INPUT_FORMAT}
          className="input input-primary"
        />
        <div className="btn btn-circle btn-ghost text-primary p-1" onClick={handleShowDatePicker}>
          <ImageIcon src="/assets/images/Icon_Calendar.png" />
        </div>
      </div>
      {showCalendar && (
        <div className="absolute top-[100%] mt-2 left-0 z-10">
          <SimpleCalendar />
        </div>
      )}
    </div>
  );
};

export default CustomDatePicker;
